import { FEATURE_FLAGS } from '@mondaygpt/constants'
import { Button } from 'components/Button'
import { motion } from 'framer-motion'
import { useFeatureGate } from 'modules/feature-flags'
import { Check, ExternalLink, Loader2, RotateCcw, X } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useCreativeStore } from '../store/creative-store'
import {
  useIdeaStore,
  type IdeaPlatformResult,
  type LivePlatform,
  type PostIdea,
} from '../store/idea-store'
import { usePublishStatusStore } from '../store/publish-status-store'
import {
  livePlatformToSocial,
  PUBLISH_METADATA_KEY,
  publishCreativeOnce,
  resolveSimulatePublish,
  type SocialPlatform,
} from '../utils/publish-creative'
import { PlatformIcon, platformLabel } from 'components/PlatformIcon'
import { ReferralInvitePrompt } from 'modules/referrals/ReferralInvitePrompt'

/**
 * Shared `layoutId` between the PostModal footer "Post to N…" button and
 * this panel. Framer Motion measures both elements and morphs box+position
 * between them when the parent flips `phase`. Mirrored from
 * `PostModal.tsx`'s footer wrapper.
 */
export const POST_PUBLISH_PANEL_LAYOUT_ID = 'post-publish-cta'

/** Shared spring so the morph and the OK button feel consistent. */
const PANEL_TRANSITION = { type: 'spring' as const, stiffness: 380, damping: 32 }

export interface PublishTarget {
  platform: LivePlatform
  creativeId: string
  /** Cached at PostModal time so the dialog doesn't have to re-derive it. */
  isAsync: boolean
}

interface PostPublishPanelProps {
  idea: PostIdea
  targets: PublishTarget[]
  /** Closes the parent PostModal. */
  onDone: () => void
  /** User opted to retry the failed platforms — parent reopens its select phase. */
  onRetryFailures: (failed: LivePlatform[]) => void
}

interface RowState {
  state: 'pending' | 'publishing' | 'success' | 'failed'
  permalink?: string
  error?: string
}

/**
 * Inline morphing panel that owns the multi-platform publish experience.
 * Anchored to the bottom-right of the modal body, it shares a `layoutId`
 * with the PostModal footer "Post to N…" button — Framer Motion morphs
 * between them when PostModal flips `phase`. The panel itself is purely
 * visual chrome; all the publishing wiring (per-target kickoff, async
 * Pusher completion, idea-store patch) is identical to the previous
 * dialog implementation.
 */
export function PostPublishPanel({
  idea,
  targets,
  onDone,
  onRetryFailures,
}: PostPublishPanelProps) {
  // The morph is driven by mount/unmount at the parent. Inside this
  // component, `open` is implicitly true while we're rendered.
  const open = true
  const simulate = resolveSimulatePublish(useFeatureGate(FEATURE_FLAGS.simulatePublish))
  const setIdeaPlatformResult = useIdeaStore(s => s.setIdeaPlatformResult)
  const asyncStatuses = usePublishStatusStore(s => s.statuses)

  const [rows, setRows] = useState<Record<LivePlatform, RowState>>(
    {} as Record<LivePlatform, RowState>
  )
  const startedRef = useRef(false)

  // ─── Helper: mark a platform terminal (writes to local + idea-store) ────
  const settle = useCallback(
    (
      platform: LivePlatform,
      creativeId: string,
      result: { success: boolean; permalink?: string; error?: string; retryable?: boolean }
    ) => {
      setRows(prev => ({
        ...prev,
        [platform]: {
          state: result.success ? 'success' : 'failed',
          permalink: result.permalink,
          error: result.error,
        },
      }))
      const ideaResult: IdeaPlatformResult = {
        creativeId,
        state: result.success ? 'success' : 'failed',
        permalink: result.permalink,
        error: result.error,
        retryable: result.retryable,
      }
      setIdeaPlatformResult(idea.projectId, platform, ideaResult)
    },
    [idea.projectId, setIdeaPlatformResult]
  )

  // ─── Kick-off (idempotent — only on first open) ─────────────────────────
  useEffect(() => {
    if (!open || startedRef.current || targets.length === 0) return
    startedRef.current = true

    // Seed every row as 'publishing' immediately so the user sees activity.
    setRows(() => {
      const initial = {} as Record<LivePlatform, RowState>
      for (const t of targets) initial[t.platform] = { state: 'publishing' }
      return initial
    })

    for (const target of targets) {
      const channel = livePlatformToSocial(target.platform)
      if (!channel) {
        // unknown channel — surface as failed so the user is aware.
        settle(target.platform, target.creativeId, {
          success: false,
          error: `${platformLabel(target.platform)} cannot be published from here`,
        })
        continue
      }

      void publishCreativeOnce({
        creativeId: target.creativeId,
        channelType: channel,
        source: 'post-modal',
        simulate,
      }).then(result => {
        if (result.kind === 'sync') {
          settle(target.platform, target.creativeId, {
            success: result.success,
            permalink: result.permalink,
            error: result.error,
            retryable: result.retryable,
          })
        }
        // async-pending: rely on the asyncStatuses effect below to settle.
      })
    }
  }, [open, targets, simulate, settle])

  // ─── Async path: watch publish-status-store for Pusher completion ────────
  useEffect(() => {
    if (!open) return
    for (const target of targets) {
      if (!target.isAsync) continue
      const row = rows[target.platform]
      if (row && (row.state === 'success' || row.state === 'failed')) continue
      const status = asyncStatuses[target.creativeId]
      if (!status) continue
      if (status.status === 'success' || status.status === 'failed') {
        const channel = livePlatformToSocial(target.platform)
        const permalink = channel
          ? extractPermalinkFromCreative(target.creativeId, channel)
          : undefined
        settle(target.platform, target.creativeId, {
          success: status.status === 'success',
          permalink,
          error: status.error,
          retryable: status.retryable,
        })
      }
    }
  }, [open, targets, asyncStatuses, rows, settle])

  // ─── Reset when the dialog closes ───────────────────────────────────────
  useEffect(() => {
    if (open) return
    startedRef.current = false
    setRows({} as Record<LivePlatform, RowState>)
  }, [open])

  // ─── Aggregate state ────────────────────────────────────────────────────
  const aggregate = useMemo(() => {
    const list = targets.map(t => rows[t.platform])
    const allTerminal =
      list.length > 0 && list.every(r => r?.state === 'success' || r?.state === 'failed')
    const anyFailed = list.some(r => r?.state === 'failed')
    const failedPlatforms = targets
      .filter(t => rows[t.platform]?.state === 'failed')
      .map(t => t.platform)
    return { allTerminal, anyFailed, failedPlatforms }
  }, [rows, targets])

  // ─── Title ──────────────────────────────────────────────────────────────
  const title = !aggregate.allTerminal
    ? 'Publishing your post'
    : aggregate.anyFailed
      ? 'Posted with errors'
      : 'Posted!'

  return (
    <motion.div
      layoutId={POST_PUBLISH_PANEL_LAYOUT_ID}
      transition={PANEL_TRANSITION}
      // Anchored bottom-right of the modal-body container — same corner the
      // footer button sits in, so the morph reads as "the button grew up
      // and to the left." `pointer-events-auto` re-enables clicks on this
      // subtree even though Body sets `pointer-events-none` to lock the
      // platform list + preview while the panel is up.
      className="pointer-events-auto absolute right-3 bottom-3 z-30 flex w-[calc(100%-1.5rem)] max-w-[380px] flex-col overflow-hidden rounded-[12px] border border-line-bright bg-bg-3 shadow-[var(--shadow-card)]"
    >
      <motion.div
        // Inner content fades in slightly delayed so it appears once the
        // box has mostly grown — keeps the morph from looking jumpy.
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.15, duration: 0.18 }}
        className="flex flex-col"
      >
        <div className="border-b border-line px-4 py-3">
          <div className="text-[13px] font-medium tracking-tight text-paper">{title}</div>
        </div>

        <div className="flex flex-col gap-2 p-3">
          {targets.map(target => (
            <ProgressRow
              key={target.platform}
              platform={target.platform}
              row={rows[target.platform]}
              onRetry={() => onRetryFailures([target.platform])}
            />
          ))}
          {/* Post-publish share nudge — only on a clean success, and only when
              the referral program is enabled (the prompt self-hides otherwise). */}
          {aggregate.allTerminal && !aggregate.anyFailed && (
            <ReferralInvitePrompt surface="post_publish_success" />
          )}
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-line bg-bg-2 px-4 py-3">
          {aggregate.anyFailed && aggregate.allTerminal ? (
            <button
              type="button"
              onClick={() => onRetryFailures(aggregate.failedPlatforms)}
              className="inline-flex items-center gap-1 text-xs text-paper-dim underline-offset-2 hover:text-paper hover:underline"
            >
              <RotateCcw size={12} /> Retry failed ({aggregate.failedPlatforms.length})
            </button>
          ) : (
            <span />
          )}
          <Button
            type="button"
            variant="t3-primary"
            size="t3-sm"
            disabled={!aggregate.allTerminal}
            onClick={onDone}
          >
            {aggregate.allTerminal ? (
              'OK'
            ) : (
              <>
                <Loader2 size={12} className="animate-spin" /> Publishing…
              </>
            )}
          </Button>
        </div>
      </motion.div>
    </motion.div>
  )
}

// ─── Internal row ──────────────────────────────────────────────────────────

function ProgressRow({
  platform,
  row,
  onRetry,
}: {
  platform: LivePlatform
  row: RowState | undefined
  onRetry: () => void
}) {
  const label = platformLabel(platform)
  const state = row?.state ?? 'pending'

  return (
    <div className="flex items-center gap-3 rounded-[10px] border border-line bg-bg-3 px-3 py-2.5">
      <PlatformIcon channel={platform} size={28} />

      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-medium tracking-tight text-paper">{label}</div>
        {state === 'failed' && row?.error && (
          <div className="mt-0.5 truncate text-[11px] text-paper-dim" title={row.error}>
            {row.error}
          </div>
        )}
      </div>
      {state === 'pending' || state === 'publishing' ? (
        <Loader2 size={14} className="shrink-0 animate-spin text-paper-dim" />
      ) : state === 'success' ? (
        row?.permalink ? (
          // Subtle View link — acid is reserved for the OK CTA. A tiny acid
          // Check is the only brand accent here so the success state is
          // legible without flooding the panel with yellow pills.
          <a
            href={row.permalink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex shrink-0 items-center gap-1 rounded-full border border-line-bright bg-bg-4 px-2 py-0.5 text-[11px] font-medium text-paper hover:bg-bg-3"
            title={row.permalink}
          >
            <Check size={11} strokeWidth={3} className="text-acid" />
            View
            <ExternalLink size={10} className="text-paper-dim" />
          </a>
        ) : (
          <Check size={14} strokeWidth={3} className="shrink-0 text-acid" />
        )
      ) : (
        <button
          type="button"
          onClick={onRetry}
          className="inline-flex shrink-0 items-center gap-1 rounded-full bg-red-500/15 px-2 py-0.5 text-[11px] font-medium text-paper-dim text-red-400 hover:text-paper"
          title={row?.error}
        >
          <X size={11} strokeWidth={3} /> Retry
        </button>
      )}
    </div>
  )
}

/**
 * After Pusher fires `creative:publish:complete`, `ChatPusherHandler` writes
 * the publish metadata onto the creative store. Read it back here so the
 * "View" link in the success row points at the real permalink.
 */
function extractPermalinkFromCreative(
  creativeId: string,
  channel: SocialPlatform
): string | undefined {
  const creative = useCreativeStore.getState().creatives.find(c => c.id === creativeId)
  if (!creative) return undefined
  const metaKey = PUBLISH_METADATA_KEY[channel]
  const content = creative.content as unknown as Record<string, unknown>
  const meta = content?.[metaKey] as { permalink?: string } | undefined
  return meta?.permalink
}
